import React, { useState } from "react";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import api from "../lib/api";

export default function MessageComposer({ open, onClose, recipient }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open || !recipient) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login"); return; }
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/messages", { recipient_id: recipient.id, content: text });
      toast.success(t("message_sent"));
      setText("");
      onClose();
      nav(`/messages?to=${recipient.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="compose-modal"
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4"
      style={{ zIndex: 10001 }}
      onClick={onClose}
    >
      <div className="bg-white rounded-lg max-w-md w-full p-6 border border-[#E6E2D8]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="overline text-[#143122]">{t("private_message")}</div>
            <h3 className="mt-1 font-serif-display text-2xl text-[#143122]">
              {t("message_to")} {recipient.name}
            </h3>
            <p className="text-xs text-[#1A1F1C]/60 mt-1">{t("private_note")}</p>
          </div>
          <button data-testid="compose-close" onClick={onClose} className="text-[#1A1F1C]/60 hover:text-[#143122]">
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <textarea
            data-testid="compose-textarea"
            required
            rows={5}
            className="kav-input"
            placeholder={t("write_message")}
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoFocus
          />
          <button data-testid="compose-send" type="submit" disabled={busy || !text.trim()} className="kav-btn-primary w-full justify-center">
            <Send size={14} strokeWidth={1.75} /> {busy ? "…" : t("send_message")}
          </button>
        </form>
      </div>
    </div>
  );
}
